import { defaultNodes, defaultLinks } from './document.mjs'
export const defaultMotifs = [
  { id: 'basic', name: '单一论证', english: 'Single argument', nodes: 2, edges: [[1, 2]], mode: 'single' },
  {
    id: 'linked',
    name: '联合论证',
    english: 'Linked argument',
    nodes: 3,
    edges: [
      [1, 3],
      [2, 3],
    ],
    mode: 'joint',
  },
  {
    id: 'multiple',
    name: '汇聚论证',
    english: 'Convergent argument',
    nodes: 3,
    edges: [
      [1, 3],
      [2, 3],
    ],
    mode: 'independent',
  },
  {
    id: 'serial',
    name: '串行论证',
    english: 'Serial argument',
    nodes: 3,
    edges: [
      [1, 2],
      [2, 3],
    ],
    mode: 'any',
  },
  {
    id: 'divergent',
    name: '发散论证',
    english: 'Divergent support',
    nodes: 3,
    edges: [
      [1, 2],
      [1, 3],
    ],
    mode: 'any',
  },
]

const legacyMotifs = [
  { id: 'basic', name: '基础论证', english: 'Basic argument', nodes: 2, edges: [[2, 1]], mode: 'any' },
  {
    id: 'linked',
    name: '联合支撑',
    english: 'Linked support',
    nodes: 3,
    edges: [
      [2, 1],
      [3, 1],
    ],
    mode: 'joint',
  },
  {
    id: 'multiple',
    name: '多重支撑',
    english: 'Multiple support',
    nodes: 3,
    edges: [
      [2, 1],
      [3, 1],
    ],
    mode: 'independent',
  },
  {
    id: 'serial',
    name: '串行支撑',
    english: 'Serial support',
    nodes: 3,
    edges: [
      [3, 2],
      [2, 1],
    ],
    mode: 'any',
  },
  { id: 'example', name: '例证支撑', english: 'Example support', nodes: 2, edges: [[2, 1]], mode: 'example' },
]

// Upgrade untouched built-ins only; retain user-authored definitions and constraints.
const sameDefinition = (a, b) =>
  !!a &&
  !!b &&
  Object.keys(a).length === Object.keys(b).length &&
  Object.keys(b).every((key) => JSON.stringify(a[key]) === JSON.stringify(b[key]))
export function upgradeMotifs(motifs) {
  return motifs
    .map((motif) => {
      const index = legacyMotifs.findIndex((legacy) => sameDefinition(motif, legacy))
      return index < 0 ? motif : JSON.parse(JSON.stringify(defaultMotifs[index]))
    })
    .filter((motif, index, list) => list.findIndex((other) => other.id === motif.id) === index)
}

function singleMatch(document, nodeIds, edgeIds) {
  if (nodeIds.length !== 2 || edgeIds.length !== 1) return false
  const ids = new Set(nodeIds)
  const incident = document.edges.filter((edge) => ids.has(edge.source) || ids.has(edge.target))
  return (
    incident.length === 1 &&
    incident[0].id === edgeIds[0] &&
    incident[0].source !== incident[0].target &&
    ids.has(incident[0].source) &&
    ids.has(incident[0].target)
  )
}

export function findMotifs(
  document,
  motif,
  limit = 250,
  schemas = defaultNodes,
  links = defaultLinks,
  fullDocument = document,
) {
  if (!motif) return []
  const evidenceTypes = new Set([
    'evidence',
    ...schemas.filter((s) => (s.legacyId || s.id) === 'evidence').map((s) => s.id),
  ])
  const supportTypes = new Set([
    'support',
    ...links.filter((s) => (s.legacyId || s.id) === 'support').map((s) => s.id),
  ])
  const edges = document.edges.filter((e) => !motif.linkType || e.type === motif.linkType)
  const types = new Map([...document.paragraphs, ...document.units].map((n) => [n.id, n.type]))
  const lookup = new Map([...document.paragraphs, ...document.units].map((n) => [n.id, n]))
  const accepts = (slot, id) => {
    const node = lookup.get(id)
    if (!node) return false
    const type = motif.nodeTypes?.[slot]
    if (type && node.type !== type) return false
    const facet = motif.nodeFacets?.[slot]
    if (facet?.id && !(node.facets?.[facet.id] || []).some((v) => !facet.value || v.value === facet.value))
      return false
    return true
  }
  const matches = [],
    seen = new Set()
  const visit = (index, mapping, usedEdges) => {
    if (matches.length >= limit) return
    if (index === motif.edges.length) {
      if (
        motif.mode === 'single' &&
        !singleMatch(
          fullDocument,
          [...mapping.values()],
          usedEdges.map((e) => e.id),
        )
      )
        return
      if (
        motif.mode === 'joint' &&
        (!usedEdges[0].joint || !usedEdges.every((e) => e.joint === usedEdges[0].joint))
      )
        return
      if (
        motif.mode === 'independent' &&
        usedEdges.some((e, i) => e.joint && usedEdges.some((other, j) => i !== j && e.joint === other.joint))
      )
        return
      if (
        motif.mode === 'example' &&
        (!evidenceTypes.has(types.get(mapping.get(2))) || !supportTypes.has(usedEdges[0].type))
      )
        return
      const key =
        [...mapping.values()].sort().join('|') +
        ':' +
        usedEdges
          .map((e) => e.id)
          .sort()
          .join('|')
      if (!seen.has(key)) {
        seen.add(key)
        matches.push({ nodeIds: [...mapping.values()], edgeIds: usedEdges.map((e) => e.id) })
      }
      return
    }
    const [a, b] = motif.edges[index]
    for (const edge of edges) {
      if (motif.edgeTypes?.[index] && edge.type !== motif.edgeTypes[index]) continue
      if (!accepts(a, edge.source) || !accepts(b, edge.target)) continue
      if (usedEdges.includes(edge) || (usedEdges.length && usedEdges[0].level !== edge.level)) continue
      if (
        (mapping.has(a) && mapping.get(a) !== edge.source) ||
        (mapping.has(b) && mapping.get(b) !== edge.target)
      )
        continue
      const used = new Set(mapping.values())
      if ((!mapping.has(a) && used.has(edge.source)) || (!mapping.has(b) && used.has(edge.target))) continue
      const next = new Map(mapping)
      next.set(a, edge.source)
      next.set(b, edge.target)
      visit(index + 1, next, [...usedEdges, edge])
      if (matches.length >= limit) return
    }
  }
  visit(0, new Map(), [])
  return matches
}

// New workflows publish matches explicitly. Editing a motif never runs analysis implicitly.
export function displayedMatches(
  document,
  motif,
  limit = 250,
  schemas = defaultNodes,
  links = defaultLinks,
  fullDocument = document,
) {
  if (!motif) return []
  if (document.engineVersion !== 2) return findMotifs(document, motif, limit, schemas, links, fullDocument)
  const published = document.motifAnalysis?.definitions
  // A shipped preset upgrade can reuse an analyzed graph with the corrected rules.
  // Custom edits still require explicit publication.
  const legacy = legacyMotifs.find(
    (old, index) => sameDefinition(defaultMotifs[index], motif) && sameDefinition(published?.[old.id], old),
  )
  if (legacy) return findMotifs(document, motif, limit, schemas, links, fullDocument)
  if (JSON.stringify(published?.[motif.id]) !== JSON.stringify(motif)) return []
  const nodes = new Set([...document.paragraphs, ...document.units].map((n) => n.id))
  const edges = new Set(document.edges.map((e) => e.id))
  return (document.motifAnalysis?.results?.find((r) => r.id === motif.id)?.matches || [])
    .filter((m) => m.nodeIds.every((id) => nodes.has(id)) && m.edgeIds.every((id) => edges.has(id)))
    .filter((m) => motif.mode !== 'single' || singleMatch(fullDocument, m.nodeIds, m.edgeIds))
    .slice(0, limit)
}
